import {
  FC,
  JSXElementConstructor,
  useEffect,
  useRef,
  /* useEffect, */ useState,
} from "react";
import {
  Asset,
  Network,
  networkConfirmationTime,
  networkExplorerLabel,
  /* Network, */ networkLabel,
  networkTxLink,
  TxResult,
} from "rujira.js";
import { i18n } from "../../i18n";
import { IconDenom } from "../icons/IconDenom";
import {
  /* AngleDown, */ AngleLeft,
  External,
  MinusCircle,
} from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { DecimalInput } from "../inputs/DecimalInput";
import { Input } from "../inputs/Input";
/* import { useClickOutside } from "../../hooks/useClickOutside";
import { BreakPoints, useWindowSize } from "../../hooks/useWindowSize";
import { AnimatePresence, motion } from "motion/react"; */
import { formatDuration, intervalToDuration } from "date-fns";
import toast from "react-hot-toast";
import { AccountProvider } from "../../wallets";
import { Button } from "../buttons/Button";
import { Decimal } from "../numbers/Decimal";
import { Warning } from "../notices/Warning";
import network from "../../assets/images/network.gif";
import deposited from "../../assets/images/deposited.gif";
import { Select } from "../inputs/Select";

export const DepositModal = <T extends string>({
  title = "Deposit",
  assets,
  targets,
  Submit,
  selected,
  getBalance,
  accountProvider,
  dismiss,
}: {
  title?: string;
  assets: Asset<T>[];
  targets: string[];
  selected?: Asset<T>;
  getBalance?: (a: Asset<T>) => bigint;
  Submit: JSXElementConstructor<{
    selected?: Asset<T>;
    amount: bigint;
    target?: string;
    onSuccess: (tx: TxResult) => void;
  }>;
  dismiss: () => void;
  accountProvider: AccountProvider;
}) => {
  const [token, setToken] = useState<string | undefined>(
    selected ? selected.metadata.symbol : undefined
  );
  const [asset, setAsset] = useState<Asset<T> | undefined>(
    selected || undefined
  );
  const [tx, setTx] = useState<TxResult>();

  const tokens = assets
    .reduce(
      (a: string[], v) =>
        a.includes(v.metadata.symbol) ? a : [v.metadata.symbol, ...a],
      []
    )
    .sort((a, b) => a.localeCompare(b));

  const networks = assets
    .filter((a) => a.metadata.symbol === token)
    .sort((a, b) => networkLabel(a.chain).localeCompare(networkLabel(b.chain)));

  return (
    <div className="deposit-modal">
      {(token || asset) && (
        <i18n.button
          className="transparent color-white hover-primary1 flex ai-c mb-1 fs-14 fw-500 pointer"
          onClick={() => {
            if (asset) setAsset(undefined);
            else setToken(undefined);
          }}>
          <AngleLeft className="block w-2 h-2 mr-0.5" />
          Back
        </i18n.button>
      )}
      <h3 className="h3">
        {i18n.t(title)}
        {token && " " + token}
      </h3>
      {!token && <TokenSelect options={tokens} onSelect={setToken} />}
      {token && !asset && (
        <NetworkSelect options={networks} onSelect={setAsset} />
      )}
      {asset && token && !tx && (
        <>
          <Confirm
            asset={asset}
            targets={targets}
            Submit={Submit}
            getBalance={getBalance}
            accountProvider={accountProvider}
            onSuccess={setTx}
          />
        </>
      )}
      {tx && <Success tx={tx} dismiss={dismiss} />}
    </div>
  );
};

const TokenSelect: FC<{ options: string[]; onSelect: (v: string) => void }> = ({
  options,
  onSelect,
}) => {
  const [search, setSearch] = useState<string>("");
  const filtered = options.filter((i) =>
    i.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Input
        label="Search"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}>
        {search !== "" && (
          <MinusCircle
            className="input-container__clear"
            onClick={() => setSearch("")}
          />
        )}
      </Input>
      <div className="deposit-modal__content row wrap pad-mini mt-1.5">
        {filtered.map((i) => (
          <div className="col-6" key={i}>
            <button
              className="card card--hover-border mt-0.5 mb-0.5 p-2 flex ai-c"
              onClick={() => {
                onSelect(i);
                setSearch("");
              }}>
              <IconDenom denom={i} className="block w-4 h-4" />
              <p className="ml-1 fw-600 color-white">{i}</p>
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <i18n.p className="color-grey my-2 w-full text-center">
            No results found
          </i18n.p>
        )}
      </div>
    </>
  );
};

const NetworkSelect = <T extends string>({
  options,
  onSelect,
}: {
  options: Asset<T>[];
  onSelect: (a: Asset<T>) => void;
}) => {
  const [search, setSearch] = useState<string>("");
  const filtered = options.filter((i) =>
    networkLabel(i.chain).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <i18n.h3 className="flex ai-c fs-20 lh-26 fw-400 mb-2">
        Select Network
      </i18n.h3>
      <Input
        label="Search"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <div className="deposit-modal__content row wrap pad-mini mt-1.5">
        {filtered.map((i) => (
          <div className="col-6" key={i.chain}>
            <button
              className="card card--hover-border mt-0.5 mb-0.5 p-2 flex ai-c"
              onClick={() => {
                onSelect(i);
                setSearch("");
              }}>
              <NetworkIcon network={i.chain} className="block w-4 h-4" />
              <p className="ml-1 fw-600 color-white">{networkLabel(i.chain)}</p>
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <i18n.p className="color-grey my-2 w-full text-center">
            No results found
          </i18n.p>
        )}
      </div>
    </>
  );
};

const Confirm = <T extends string>({
  getBalance,
  asset,
  targets,
  Submit,
  accountProvider,
  onSuccess,
}: {
  getBalance?: (a: Asset<T>) => bigint;
  asset: Asset<T>;
  targets: string[];
  Submit: JSXElementConstructor<{
    selected?: Asset<T>;
    amount: bigint;
    target?: string;
    onSuccess: (tx: TxResult) => void;
  }>;
  onSuccess: (tx: TxResult) => void;
  accountProvider: AccountProvider;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [amount, setAmount] = useState(0n);
  const [target, setTarget] = useState<string>(targets[0] || "");
  const [custom, setCustom] = useState<string>("");
  const { selected, select, accounts } = accountProvider;

  const sender =
    selected?.network === asset.chain
      ? selected
      : accounts?.find((a) => a.network === asset.chain);

  useEffect(() => {
    if (ref.current && target === "custom") {
      ref.current.focus();
    }
  }, [target, ref]);

  return (
    <>
      <h3 className="flex ai-c fs-20 lh-26 fw-400">
        <IconDenom
          denom={asset.metadata.symbol}
          className="block w-3 h-3 mr-1"
        />
        from
        <NetworkIcon
          network={asset.chain}
          className="block w-4 h-4 ml-1 mr-0.5"
        />
        <span className="fw-600">{networkLabel(asset.chain)}</span>
      </h3>
      {getBalance ? (
        <div className="flex ai-c mt-1 fs-14 condensed color-grey">
          {selected?.network !== asset.chain && sender && (
            <button
              className="transparent flex ai-c color-orange"
              onClick={() => select(sender)}>
              <Warning
                className="warning--xs condensed flex ai-c mb-1"
                color="orange">
                <img src={network} alt="network" />
                <div className="text-left">
                  Switch to the{" "}
                  <span className="color-white">
                    {networkLabel(asset.chain)}
                  </span>{" "}
                  network
                </div>
              </Warning>
            </button>
          )}
          {selected?.network === asset.chain && sender && (
            <div
              data-tooltip-content={sender.address}
              data-tooltip-id="modal-tip"
              onClick={() => {
                toast.success("Address copied to clipboard");
                navigator.clipboard.writeText(sender.address);
              }}
              className="pointer">
              From:{" "}
              <span className="color-white ml-0.5">
                {sender.address.substring(0, 8) +
                  "..." +
                  sender.address.slice(-12)}
              </span>
            </div>
          )}
          <div
            className="ml-a py-1 px-0.5 pointer"
            onClick={() => {
              setAmount(getBalance(asset));
            }}>
            Balance:{" "}
            <span className="color-white">
              <Decimal
                amount={getBalance(asset)}
                decimals={asset.metadata.decimals}
              />
            </span>
          </div>
        </div>
      ) : null}
      <DecimalInput
        className="numeric-input numeric-input--white numeric-input--full condensed"
        amount={amount}
        decimals={asset.metadata.decimals}
        onAmountchange={setAmount}
      />
      <Select
        className="mt-1.5"
        value={target}
        onChange={(e) => setTarget(e.currentTarget.value)}
        label={target ? "Destination" : undefined}>
        <i18n.option disabled value="">
          Select Destination...
        </i18n.option>
        {targets.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
        <i18n.option value="custom">Custom address</i18n.option>
      </Select>
      {target === "custom" && (
        <Input
          value={custom}
          onChange={(e) => setCustom(e.currentTarget.value)}
          label="Destination Address"
          className="mt-1.5"
          innerRef={ref}
        />
      )}
      {/* <Destination value={target}>
        {targets.map((x) => (
          <DestinationItem key={x} value={x} onClick={() => setTarget(x)} />
        ))}
      </Destination> */}
      <Submit
        selected={asset}
        amount={amount}
        target={target !== "custom" ? target : custom}
        onSuccess={onSuccess}
      />
    </>
  );
};

const Success: FC<{ tx: TxResult; dismiss: () => void }> = ({
  tx,
  dismiss,
}) => {
  return (
    <>
      <div className="mt-2 flex ai-c br-2 bg-teal-10 p-2">
        <img
          src={deposited}
          alt=""
          className="filter-teal block w-6 h-a no-shrink"
        />
        <span className="color-teal fs-18 fw-600 mx-1">Deposit Successful</span>
      </div>
      <div className="table table--no-hover mt-1">
        <tbody>
          <tr>
            <td className="color-grey">
              {networkLabel(tx.network)} deposit ETA:
            </td>
            <td>
              {formatDuration(
                intervalToDuration({
                  start: 0,
                  end: networkConfirmationTime(tx.network),
                }),
                { format: ["hours", "minutes", "seconds"], zero: false }
              )}
            </td>
          </tr>
          <tr>
            <td className="color-grey">View TX:</td>
            <td>
              <a
                href={networkTxLink(tx)}
                target="_blank"
                className="color-white hover-primary1 no-underline">
                {networkExplorerLabel(tx.network)}
                <External className="inline-block w-1.5 h-1.5 ml-0.5" />
              </a>
            </td>
          </tr>
          <tr>
            <td className="color-grey">Track Deposit:</td>
            <td>
              <a
                href={networkTxLink({
                  network: Network.Thorchain,
                  txHash: tx.txHash.replace(/^0x/, "").toUpperCase(),
                })}
                target="_blank"
                className="color-white hover-primary1 no-underline">
                THORChain.net
                <External className="inline-block w-1.5 h-1.5 ml-0.5" />
              </a>
            </td>
          </tr>
        </tbody>
      </div>
      <div className="modal__footer p-3 text-right">
        <Button label="Close" onClick={dismiss} />
      </div>
    </>
  );
};
